import Sentry from "@similarweb/sentry-logger";

import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getSafeHref, isAbsoluteHref, SAFE_HREF_FALLBACK } from "../../helpers/onboardingAgentHelpers";
import { ONBOARDING_AGENT_LOG_PREFIX } from "../../onboardingAgentConstants";

const SUGGESTED_PAGE_NAV_FALLBACK_PARAMS: Record<string, unknown> = {
    isWWW: "*",
    country: 999,
    countryCode: 999,
    duration: "3m",
    webSource: devicesTypes.TOTAL,
};

export type SuggestedPageNavigationResult =
    | {
          readonly type: "in-app";
          readonly didNavigate: boolean;
      }
    | {
          readonly type: "external" | "redirect" | "none";
          readonly didNavigate: false;
      };

const isUiRouterStateNavigable = async (uiRouterState: string): Promise<boolean> => {
    try {
        // Concrete states of lazy-loaded modules are registered only after this
        // resolves. Without it, a valid-but-unloaded state still resolves to its
        // "**" future-state placeholder and would be wrongly treated as missing.
        await proNavigator.lazyLoadIfNeeded(uiRouterState);
    } catch (error) {
        Sentry.warning(
            `${ONBOARDING_AGENT_LOG_PREFIX} Failed to lazy-load suggested page state "${uiRouterState}": ${String(error)}`,
        );

        return false;
    }

    // getState() falls back to the matching "**" future-state placeholder for
    // unregistered names, so require an exact match to confirm the state really
    // exists on the client.
    return proNavigator.getState(uiRouterState)?.name === uiRouterState;
};

const navigateViaUiRouterState = async (
    uiRouterState: string,
    params?: Readonly<Record<string, unknown>>,
): Promise<SuggestedPageNavigationResult | null> => {
    if (!(await isUiRouterStateNavigable(uiRouterState))) {
        Sentry.warning(
            `${ONBOARDING_AGENT_LOG_PREFIX} Suggested page uiRouterState "${uiRouterState}" is not registered; falling back to link`,
        );

        return null;
    }

    const itemParams = proNavigator.getItemParams({ state: uiRouterState });

    try {
        await proNavigator.go(uiRouterState, {
            ...SUGGESTED_PAGE_NAV_FALLBACK_PARAMS,
            ...itemParams,
            ...params,
        });

        return { type: "in-app", didNavigate: true };
    } catch (error) {
        Sentry.error(`${ONBOARDING_AGENT_LOG_PREFIX} Suggested page navigation failed`, error as Error);

        return null;
    }
};

const navigateViaLink = (link: string): SuggestedPageNavigationResult => {
    const safeLink = getSafeHref(link);

    if (safeLink === SAFE_HREF_FALLBACK) {
        Sentry.warning(`${ONBOARDING_AGENT_LOG_PREFIX} Rejected suggested page navigation to unsafe link: ${link}`);

        return { type: "none", didNavigate: false };
    }

    if (isAbsoluteHref(safeLink)) {
        window.open(safeLink, "_blank", "noopener,noreferrer");

        return { type: "external", didNavigate: false };
    }

    window.location.assign(safeLink);

    return { type: "redirect", didNavigate: false };
};

export type SuggestedPageNavigationTarget = {
    readonly uiRouterState: string;
    readonly link?: string;
    readonly params?: Readonly<Record<string, unknown>>;
};

export const goToSuggestedPage = async ({
    uiRouterState,
    link,
    params,
}: SuggestedPageNavigationTarget): Promise<SuggestedPageNavigationResult> => {
    if (uiRouterState) {
        const result = await navigateViaUiRouterState(uiRouterState, params);

        if (result) {
            return result;
        }
    }

    if (link) {
        return navigateViaLink(link);
    }

    return { type: "none", didNavigate: false };
};
